/**
 * ComposicaoPrecoDivisor — Composicao de preco do motor DIVISOR (ADR-0050).
 *
 * Renderiza o payload de GET /api/orcamentos/{id}/preco (shape unico, Q6):
 * TVC (com drill-down de terceiros), custo carregado (k), s decomposto,
 * L, divisor, PRECO PISO em destaque, campo de preco final com semaforo
 * (textos canonicos da spec §3.1-E) e T (throughput) sempre visivel.
 *
 * NAO decide nada: piso, minimo e faixa sao do backend; o semaforo local
 * (Number) e so feedback imediato ao digitar — o PATCH /preco-final revalida
 * tudo em Decimal e e quem grava a faixa/alcada.
 *
 * Concorrencia (R17): o PATCH envia o `updated_at` lido do orcamento;
 * 409 vira banner "outra pessoa alterou este orcamento — recarregue",
 * nunca retry silencioso.
 *
 * So aparece em orcamento com motor_preco = DIVISOR — CASCATA continua
 * renderizando o ComposicaoPreco antigo (o discriminador aparece na tela).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiInfo, FiLock, FiRefreshCw } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import AsyncButton from "../../../components/AsyncButton";
import EmptyState from "../../../components/EmptyState";
import { MiniSpinner } from "../../../components/LoadingMarcenaria";
import { useAuth } from "../../../context/AuthContext";
import { getToneTokens } from "../../../styles/shared";
import {
  getPrecoOrcamento,
  getSugestaoK,
  patchPrecoFinal,
} from "../../../services/motorPreco";

import { fmtMoeda, fmtNumero } from "../helpers";
import {
  CascataCard,
  CascataDivider,
  CascataDividerStrong,
  CascataGrid,
  CascataHeader,
  CascataInlineNote,
  CascataLabelMuted,
  CascataLinkButton,
  CascataSubtotalLabel,
  CascataSubtotalValue,
  CascataTitle,
  CascataTotalLabel,
  CascataValue,
  ComposicaoTable,
  Input,
  Pill,
  SecondaryButton,
  Textarea,
  WarningMsg,
} from "../styles";

const MOTOR_DIVISOR = "DIVISOR";

// Mensagem do semaforo — cor SOMENTE pela paleta de estado do tema.
const SemaforoMsg = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  background: ${({ theme, $tone }) => getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone }) => getToneTokens(theme, $tone).text};
  border: 1px solid ${({ theme, $tone }) => getToneTokens(theme, $tone).border};
`;

const ContaMono = styled.div`
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.textTertiary};
  white-space: pre-wrap;
`;

const PisoDestaque = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  text-align: right;
`;

const LinhaPrecoFinal = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Aceita "15.133,80", "15133,80", "15133.80" e "R$ 15.133,80".
function parsePrecoBR(txt) {
  if (txt === null || txt === undefined) return NaN;
  const s = String(txt).trim().replace(/^R\$\s?/i, "");
  if (!s) return NaN;
  if (s.includes(",")) return Number(s.replace(/\./g, "").replace(",", "."));
  return Number(s);
}

function fmtPrecoBR(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return fmtNumero(n, 2);
}

export default function ComposicaoPrecoDivisor({ orcamento, editavel = true, onChange }) {
  const { request, hasAllPermissions, isMaster } = useAuth();
  const navigate = useNavigate();

  const [preco, setPreco] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);
  const [sugestaoK, setSugestaoK] = useState(null);
  const [precoEdit, setPrecoEdit] = useState("");
  // "o vendedor digitou e ainda nao salvou" — usado para a recarga nao
  // sobrescrever o campo (ref em vez de state: e lido dentro do callback
  // de carga sem re-armar o efeito).
  const precoEditSujoRef = useRef(false);
  const [justificativa, setJustificativa] = useState("");
  const [conflito, setConflito] = useState(false);
  const [erroSalvar, setErroSalvar] = useState(null);
  const [terceirosAbertos, setTerceirosAbertos] = useState(false);

  const podeConfigurar = isMaster || hasAllPermissions(["comercial:configurar"]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga(null);
    try {
      const payload = await getPrecoOrcamento(request, orcamento.id, {
        silentError: true,
      });
      setPreco(payload);
      // Pre-preenche com o preco ja definido; senao, com o piso (spec §3.1-E).
      // NAO sobrescreve o que o vendedor esta digitando e ainda nao salvou:
      // a recarga agora tambem dispara ao ligar a NF (V14), e engolir o
      // numero em edicao seria perder trabalho na frente do cliente.
      setPrecoEdit((atual) => {
        const emEdicao = precoEditSujoRef.current;
        if (emEdicao && atual) return atual;
        return fmtPrecoBR(payload?.preco_final_definido ?? payload?.preco_piso);
      });
    } catch (err) {
      setErroCarga(err?.message || "Não foi possível calcular o preço.");
    } finally {
      setCarregando(false);
    }
  }, [request, orcamento.id]);

  useEffect(() => {
    if (orcamento?.motor_preco !== MOTOR_DIVISOR) return;
    carregar();
    // `emitir_nf` entra nas deps porque o imposto e componente do divisor
    // (`s`): sem isso a composicao seguia exibindo "sem NF 0,00%" depois de
    // ligar a NF, ate algum outro recalculo passar por ali (auditoria
    // 2026-08-07, V14).
  }, [carregar, orcamento?.motor_preco, orcamento?.emitir_nf]);

  useEffect(() => {
    if (orcamento?.motor_preco !== MOTOR_DIVISOR) return undefined;
    // Recarga silenciosa: editores de areas/insumos/cotacoes disparam este
    // evento apos cada mutacao — o painel refaz o GET /preco sem F5.
    const handler = () => carregar();
    window.addEventListener("orc2:preco-invalidado", handler);
    return () => window.removeEventListener("orc2:preco-invalidado", handler);
  }, [carregar, orcamento?.motor_preco]);

  useEffect(() => {
    if (orcamento?.motor_preco !== MOTOR_DIVISOR) return;
    let ativo = true;
    // Selo "sugerido pelo financeiro" — melhor esforco: sem financeiro:view
    // o backend responde 403 e a tela simplesmente fica sem o selo.
    (async () => {
      try {
        const payload = await getSugestaoK(request, {}, { silentError: true });
        if (ativo) setSugestaoK(payload);
      } catch {
        if (ativo) setSugestaoK(null);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [request, orcamento?.motor_preco]);

  if (orcamento?.motor_preco !== MOTOR_DIVISOR) return null;

  if (carregando) {
    return (
      <CascataCard>
        <CascataTitle>Composição de preço</CascataTitle>
        <MiniSpinner label="Calculando preço" />
      </CascataCard>
    );
  }

  if (erroCarga || !preco) {
    // k/L nao configurados, s+L >= 0,90 etc — a mensagem do backend explica.
    return (
      <CascataCard>
        <CascataTitle>Composição de preço</CascataTitle>
        <EmptyState
          tone="locked"
          titulo="Não deu para montar o preço"
          descricao={erroCarga || "Falha ao calcular o preço."}
          acoes={
            podeConfigurar
              ? [{
                  label: "Abrir configuração comercial",
                  onClick: () => navigate("/comercial/configuracoes"),
                }]
              : []
          }
        />
      </CascataCard>
    );
  }

  const piso = toNum(preco.preco_piso);
  const minimo = toNum(preco.preco_minimo_absoluto);
  const numerador = toNum(preco.numerador);
  const tvcTotal = toNum(preco.tvc_total);
  const sTotalPct = toNum(preco.s?.total_percentual);
  const lPct = toNum(preco.margem_liquida_alvo_percentual);
  const kPct = toNum(preco.k_cobertura_percentual);
  const divisor = toNum(preco.divisor);
  const terceiros = preco.terceiros || [];

  const precoNum = parsePrecoBR(precoEdit);
  const precoValido = Number.isFinite(precoNum) && precoNum > 0;

  // Semaforo local (feedback imediato) — as contas sao as do §0.4 da spec.
  const lucroNoPiso = (lPct / 100) * piso;
  const lucroRealizado = precoValido
    ? precoNum * (1 - sTotalPct / 100) - numerador
    : null;

  let faixaLocal = null;
  if (precoValido) {
    if (precoNum >= piso) faixaLocal = "ACIMA_PISO";
    else if (precoNum >= minimo) faixaLocal = "ENTRE_MINIMO_E_PISO";
    else faixaLocal = "ABAIXO_MINIMO";
  }

  const exigeJustificativa = faixaLocal === "ENTRE_MINIMO_E_PISO"
    || faixaLocal === "ABAIXO_MINIMO";
  const justificativaCurta = faixaLocal === "ABAIXO_MINIMO"
    && justificativa.trim().length < 20;

  // T sempre visivel: usa o preco digitado; sem preco valido, cai no piso.
  const baseT = precoValido ? precoNum : piso;
  const throughput = baseT - tvcTotal;
  const throughputPct = baseT > 0 ? (throughput / baseT) * 100 : 0;

  const kSugerido = sugestaoK?.k_sugerido_percentual;

  const salvarPrecoFinal = async () => {
    setConflito(false);
    setErroSalvar(null);
    try {
      const payload = await patchPrecoFinal(
        request,
        orcamento.id,
        {
          preco_final_definido: precoNum.toFixed(2),
          updated_at: orcamento.updated_at,
          justificativa: justificativa.trim() || undefined,
        },
        { silentError: true },
      );
      setPreco(payload);
      setPrecoEdit(fmtPrecoBR(payload?.preco_final_definido));
      precoEditSujoRef.current = false; // salvo: recarga pode reassumir
      setJustificativa("");
      if (onChange) await onChange();
    } catch (err) {
      if (err?.status === 409) {
        setConflito(true);
      } else {
        setErroSalvar(err?.message || "Erro ao salvar o preço.");
      }
      throw err; // AsyncButton (silentError) engole; evita toast de sucesso.
    }
  };

  return (
    <CascataCard>
      <CascataHeader>
        <CascataTitle>Composição de preço — motor divisor</CascataTitle>
        {preco.congelado_em && (
          <Pill $tone="neutral" title={`Congelado em ${preco.congelado_em}`}>
            <FiLock size={10} /> congelado
          </Pill>
        )}
      </CascataHeader>

      {conflito && (
        <WarningMsg>
          <span>
            Outra pessoa alterou este orçamento — recarregue. O que você
            digitou continua neste navegador.
          </span>
          <SecondaryButton
            type="button"
            onClick={() => {
              carregar();
              if (onChange) onChange();
            }}
          >
            <FiRefreshCw size={12} /> Recarregar
          </SecondaryButton>
        </WarningMsg>
      )}

      {(preco.avisos || []).map((aviso) => (
        <WarningMsg key={aviso}>{aviso}</WarningMsg>
      ))}

      {/* A — TVC: o que sai do bolso */}
      <CascataGrid>
        <CascataLabelMuted title="Chapas, bordas, ferragens, acessórios e revenda — detalhe nos cards de ambiente.">
          <FiLock size={11} /> Material
        </CascataLabelMuted>
        <CascataValue>{fmtMoeda(preco.tvc_material)}</CascataValue>

        <CascataLabelMuted>
          <FiLock size={11} />
          <CascataLinkButton
            type="button"
            onClick={() => setTerceirosAbertos((v) => !v)}
            disabled={terceiros.length === 0}
          >
            Terceiros ({terceiros.length}{" "}
            {terceiros.length === 1 ? "cotação" : "cotações"})
          </CascataLinkButton>
        </CascataLabelMuted>
        <CascataValue>{fmtMoeda(preco.tvc_terceiros)}</CascataValue>

        <CascataSubtotalLabel>TVC total</CascataSubtotalLabel>
        <CascataSubtotalValue>{fmtMoeda(preco.tvc_total)}</CascataSubtotalValue>
      </CascataGrid>

      {terceirosAbertos && terceiros.length > 0 && (
        <ComposicaoTable>
          <thead>
            <tr>
              <th>Nº</th>
              <th>Tipo</th>
              <th>Situação</th>
              <th className="num">Valor</th>
              <th className="num">Fator</th>
              <th className="num">Carregado</th>
              <th className="num">× efetivo</th>
            </tr>
          </thead>
          <tbody>
            {terceiros.map((t) => (
              <tr key={t.cotacao}>
                <td>{t.cotacao}</td>
                <td>{t.tipo}</td>
                <td>
                  {t.vencida ? (
                    <Pill $tone="danger">vencida</Pill>
                  ) : (
                    <Pill $tone={t.situacao === "FIRME" ? "success" : "warning"}>
                      {t.situacao === "FIRME" ? "Firme" : "estimativa"}
                    </Pill>
                  )}
                </td>
                <td className="num">{fmtMoeda(t.valor_efetivo)}</td>
                <td className="num">×{fmtNumero(t.fator_lucro, 3)}</td>
                <td className="num">{fmtMoeda(t.contribuicao_numerador)}</td>
                <td className="num">×{fmtNumero(t.multiplicador_efetivo, 4)}</td>
              </tr>
            ))}
          </tbody>
        </ComposicaoTable>
      )}

      <CascataDivider />

      {/* B — Cobertura e carga */}
      <CascataGrid>
        <CascataLabelMuted>
          <FiLock size={11} /> Cobertura de casa (k)
          {kSugerido !== null && kSugerido !== undefined && (
            <Pill
              $tone="info"
              title={
                `OE ÷ TVC dos ${sugestaoK?.janela_meses ?? 3} meses-calendário `
                + `fechados anteriores (${sugestaoK?.periodo_inicio ?? ""} a `
                + `${sugestaoK?.periodo_fim ?? ""}). ${sugestaoK?.motivo ?? ""}`
              }
            >
              sugerido pelo financeiro: {fmtNumero(kSugerido, 2)}%
            </Pill>
          )}
        </CascataLabelMuted>
        <CascataValue>
          {fmtNumero(kPct, 2)}% (×{fmtNumero(1 + kPct / 100, 2)})
        </CascataValue>

        <CascataSubtotalLabel title="Custo carregado é rótulo de tela do numerador — não é fronteira de nada.">
          Custo carregado
        </CascataSubtotalLabel>
        <CascataSubtotalValue>{fmtMoeda(preco.numerador)}</CascataSubtotalValue>

        <CascataLabelMuted>
          <FiLock size={11} /> Multiplicador efetivo do material
        </CascataLabelMuted>
        <CascataValue>
          ×{fmtNumero(preco.multiplicador_efetivo_material, 4)}
        </CascataValue>
      </CascataGrid>

      <CascataDivider />

      {/* C — Divisor: s decomposto + L */}
      <CascataGrid>
        <CascataLabelMuted>
          <FiLock size={11} /> RT (parceiros)
        </CascataLabelMuted>
        <CascataValue>{fmtNumero(preco.s?.rt_percentual, 2)}%</CascataValue>

        <CascataLabelMuted>
          <FiLock size={11} /> Taxa do pagamento
          {!preco.condicao_pagamento && (
            <CascataInlineNote>
              escolha a condição de pagamento — boleto e dinheiro têm taxa
              zero, é escolha válida
            </CascataInlineNote>
          )}
        </CascataLabelMuted>
        <CascataValue>
          {fmtNumero(preco.s?.taxa_pagamento_percentual, 2)}%
        </CascataValue>

        <CascataLabelMuted>
          <FiLock size={11} /> Imposto
          {toNum(preco.s?.imposto_percentual) === 0 && (
            <Pill $tone="neutral">sem NF</Pill>
          )}
        </CascataLabelMuted>
        <CascataValue>{fmtNumero(preco.s?.imposto_percentual, 2)}%</CascataValue>

        <CascataSubtotalLabel>s total</CascataSubtotalLabel>
        <CascataSubtotalValue>{fmtNumero(sTotalPct, 2)}%</CascataSubtotalValue>

        <CascataLabelMuted>
          <FiLock size={11} /> Lucro líquido alvo (L)
        </CascataLabelMuted>
        <CascataValue>{fmtNumero(lPct, 2)}%</CascataValue>

        <CascataSubtotalLabel>Divisor (1 − s − L)</CascataSubtotalLabel>
        <CascataSubtotalValue>{fmtNumero(divisor, 4)}</CascataSubtotalValue>
      </CascataGrid>

      <CascataDividerStrong />

      {/* D — Piso, em destaque */}
      <CascataGrid>
        <CascataTotalLabel>PREÇO PISO</CascataTotalLabel>
        <PisoDestaque>{fmtMoeda(preco.preco_piso)}</PisoDestaque>
      </CascataGrid>
      <ContaMono>
        {`preco_piso            ${fmtMoeda(preco.numerador)} ÷ ${fmtNumero(divisor, 4)} = ${fmtMoeda(preco.preco_piso)}\n`
          + `preco_minimo_absoluto ${fmtMoeda(preco.numerador)} ÷ ${fmtNumero(1 - sTotalPct / 100, 4)} = ${fmtMoeda(preco.preco_minimo_absoluto)}   (o mesmo piso com L = 0)`}
      </ContaMono>

      <CascataDivider />

      {/* E — Preço final e semáforo */}
      <LinhaPrecoFinal>
        <CascataSubtotalLabel>Preço ao cliente</CascataSubtotalLabel>
        <Input
          style={{ width: 140, textAlign: "right" }}
          value={precoEdit}
          onChange={(e) => {
            precoEditSujoRef.current = true;
            setPrecoEdit(e.target.value);
          }}
          disabled={!editavel}
          inputMode="decimal"
          aria-label="Preço final ao cliente"
        />
        <SecondaryButton
          type="button"
          onClick={() => setPrecoEdit(fmtPrecoBR(piso))}
          disabled={!editavel}
        >
          = piso
        </SecondaryButton>
        <AsyncButton
          variant="primary"
          onClick={salvarPrecoFinal}
          disabled={!editavel || !precoValido || justificativaCurta}
          successMessage="Preço salvo."
          silentError
        >
          Salvar preço
        </AsyncButton>
      </LinhaPrecoFinal>

      {faixaLocal === "ACIMA_PISO" && (
        <SemaforoMsg $tone="success">
          {`R$ ${fmtNumero(precoNum - piso, 2)} acima do piso `}
          {piso > 0 && `(+${fmtNumero(((precoNum - piso) / piso) * 100, 2)}%).`}
        </SemaforoMsg>
      )}
      {faixaLocal === "ENTRE_MINIMO_E_PISO" && (
        <SemaforoMsg $tone="warning">
          {`Cobre custo, casa, RT, cartão e imposto, mas reduz o lucro líquido `
            + `— sobra R$ ${fmtNumero(Math.max(lucroRealizado, 0), 2)} em vez de `
            + `R$ ${fmtNumero(lucroNoPiso, 2)}. Salvar neste valor exige aprovação `
            + `de quem tem alçada de desconto.`}
        </SemaforoMsg>
      )}
      {faixaLocal === "ABAIXO_MINIMO" && (
        <SemaforoMsg $tone="danger">
          {`Não cobre nem as obrigações: faltam R$ ${fmtNumero(minimo - precoNum, 2)} `
            + `para pagar RT + cartão + imposto. Só gestor com alçada + verificação `
            + `MFA recente e justificativa de pelo menos 20 caracteres pode aprovar.`}
        </SemaforoMsg>
      )}

      {exigeJustificativa && editavel && (
        <>
          <Textarea
            placeholder={
              faixaLocal === "ABAIXO_MINIMO"
                ? "Justificativa da alçada (mínimo 20 caracteres) — fica auditada"
                : "Justificativa da alçada — fica auditada"
            }
            value={justificativa}
            onChange={(e) => setJustificativa(e.target.value)}
          />
          {justificativaCurta && (
            <CascataInlineNote>
              A justificativa para vender abaixo do mínimo precisa ter pelo
              menos 20 caracteres.
            </CascataInlineNote>
          )}
        </>
      )}

      {erroSalvar && <SemaforoMsg $tone="danger">{erroSalvar}</SemaforoMsg>}

      <CascataDividerStrong />

      {/* T — sempre visível, fora de qualquer colapso */}
      <CascataGrid>
        <CascataSubtotalLabel>
          T (throughput)
          <CascataInlineNote>
            {" "}É isso que sobra para pagar a casa e o lucro.
          </CascataInlineNote>
        </CascataSubtotalLabel>
        <CascataSubtotalValue>
          {`${fmtMoeda(baseT)} − ${fmtMoeda(tvcTotal)} = ${fmtMoeda(throughput)} `}
          ({fmtNumero(throughputPct, 1)}% do preço)
        </CascataSubtotalValue>
      </CascataGrid>

      <CascataInlineNote>
        <FiInfo size={10} /> O semáforo compara só a parte calculada pela
        fórmula. Adicionais manuais entram por cima, com o preço que você
        digitou neles.
      </CascataInlineNote>
    </CascataCard>
  );
}
